import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getBrandHost } from "@/lib/moduleHost";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/** Turn a FastAPI error `detail` into a printable string (handles both
 *  the `{msg}` and the `[{msg,...}]` array shape). */
function formatDetail(d) {
  if (d == null) return "";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e))
                                  .filter(Boolean).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`,
                        { email: email.trim().toLowerCase() },
                        { withCredentials: true });
      setSent(true);
      toast.success("Check your inbox — if the email is registered, "
                     + "a reset link is on its way.");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail)
                    || "Couldn't request a reset — try again in a moment.");
    }
    setSubmitting(false);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] dark:bg-stone-950 px-4 py-12">
      {/* iter316 — Back-to-module lives in the black top global bar
       *  (GoogleTranslateBar → ReturnToChip). Do NOT re-render it
       *  inside the card. */}
      <div
        data-testid="forgot-password-card"
        className="w-full max-w-md bg-white rounded-2xl border border-[#E8E5DF] p-8 md:p-10"
      >
        <Link
          to="/"
          className="font-serif-display text-2xl text-[#121614] hover:text-[#C05746]"
        >
          {getBrandHost()?.title || "HustleSuite"}
        </Link>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-6">
          Forgot password
        </h1>
        <p className="text-sm text-[#5C625E] mt-2">
          Enter the email on your account and we&apos;ll send you a link
          to set a new password. The link is valid for 30 minutes.
        </p>

        {sent ? (
          <div className="mt-8 space-y-4"
                data-testid="forgot-password-sent">
            <p className="text-sm text-[#121614]">
              If <strong>{email}</strong> is on file, you&apos;ll get an
              email shortly. It might land in Spam — check there too.
            </p>
            <div className="text-sm">
              <Link to="/login"
                    data-testid="forgot-password-back-login"
                    className="text-[#C05746] hover:underline">
                ← Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                data-testid="forgot-password-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-xl h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="forgot-password-submit"
              disabled={submitting || !email}
              className="w-full bg-[#121614] text-[#FDFBF7] hover:bg-[#2C3448] h-11 rounded-xl"
            >
              {submitting ? "Sending link…" : "Email me a reset link"}
            </Button>
            <div className="text-sm text-center">
              <Link to="/login"
                    data-testid="forgot-password-cancel"
                    className="text-[#5C625E] hover:text-[#C05746]">
                Cancel and go back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
